// Customer dashboard, screen 3 of 3: company/contact info + per-channel
// notification preferences (organizations.notify_email/notify_telegram/
// notify_whatsapp — migration 001, already there for the admin settings
// screen's own global defaults; this is the SAME columns' per-organization
// override, now customer-editable). GET/PUT /customer/organization
// (services/customerService.js#updateMyOrganization).
//
// Only the fields that actually changed are sent (react-hook-form's own
// isDirty per-field state via formState.dirtyFields) — same "diff before
// PUT" discipline admin/src/pages/SettingsPage.jsx already established,
// which is also what keeps the audit_log before/after entry accurate.

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../context/LanguageContext.jsx';
import PortalShell from '../components/PortalShell.jsx';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import {
  useMyOrganization,
  useUpdateMyOrganization,
  useDeleteMyAccount,
} from '../hooks/useAccount.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './AccountSettingsPage.module.css';

const DELETE_CONFIRMATION_PHRASE = 'DELETE MY ACCOUNT';
const DELETE_CODES = ['INVALID_CONFIRMATION', 'ACCOUNT_ALREADY_DELETED'];

function toFormValues(org) {
  return {
    name: org.name || '',
    tin: org.tin || '',
    contactName: org.contact_name || '',
    contactEmail: org.contact_email || '',
    contactPhone: org.contact_phone || '',
    address: org.address || '',
    notifyEmail: Boolean(org.notify_email),
    notifyTelegram: Boolean(org.notify_telegram),
    notifyWhatsapp: Boolean(org.notify_whatsapp),
  };
}

export default function AccountSettingsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgQuery = useMyOrganization();
  const updateMutation = useUpdateMyOrganization();
  const deleteMutation = useDeleteMyAccount();
  const { toastOk, toastErr } = useActionFeedback();
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({ defaultValues: toFormValues({}) });

  useEffect(() => {
    if (orgQuery.data) reset(toFormValues(orgQuery.data));
  }, [orgQuery.data, reset]);

  const onSubmit = (values) => {
    const patch = {};
    for (const key of Object.keys(dirtyFields)) {
      patch[key] = values[key];
    }
    if (Object.keys(patch).length === 0) return;

    updateMutation.mutate(patch, {
      onSuccess: (data) => {
        reset(toFormValues(data));
        toastOk(t('portal.accountSettings.savedToast'));
      },
      onError: (err) => toastErr(err, ['VALIDATION_ERROR']),
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(deleteConfirmation, {
      onSuccess: () => {
        toastOk(t('portal.accountSettings.dangerDeletedToast'));
        // The server already destroyed the session as part of this
        // response (cookie cleared) — mirror AuthContext.jsx's own
        // logoutMutation.onSuccess so the header/nav reflect "logged out"
        // immediately, with no extra round trip to /auth/me.
        queryClient.setQueryData(['auth', 'me'], null);
        queryClient.clear();
        navigate('/');
      },
      onError: (err) => toastErr(err, DELETE_CODES),
    });
  };

  return (
    <PortalShell backTo="/track" backLabelId="portal.track.backToList" width="medium">
      {orgQuery.isLoading && <LoadingState />}
      {orgQuery.isError && !orgQuery.isLoading && (
        <ErrorState messageId="portal.accountSettings.errorBody" error={orgQuery.error} />
      )}

      {!orgQuery.isLoading && !orgQuery.isError && orgQuery.data && (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <h1 className={styles.title}>{t('portal.accountSettings.title')}</h1>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('portal.accountSettings.companySection')}</h2>

            {/* Short related fields paired two-up on desktop, collapsing to one
                column under ~600px (portal/CLAUDE.md). Address spans both. */}
            <div className={styles.grid}>
              <label className={styles.field}>
                <span className={styles.label}>{t('portal.accountSettings.name')}</span>
                <input
                  type="text"
                  className={styles.input}
                  maxLength={200}
                  {...register('name', { required: true })}
                />
                {errors.name && (
                  <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
                )}
              </label>

              <label className={styles.field}>
                <span className={styles.label}>{t('portal.accountSettings.tin')}</span>
                <input type="text" className={styles.input} maxLength={20} {...register('tin')} />
              </label>

              <label className={styles.field}>
                <span className={styles.label}>{t('portal.accountSettings.contactName')}</span>
                <input
                  type="text"
                  className={styles.input}
                  maxLength={120}
                  {...register('contactName', { required: true })}
                />
                {errors.contactName && (
                  <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
                )}
              </label>

              <label className={styles.field}>
                <span className={styles.label}>{t('portal.accountSettings.contactEmail')}</span>
                <input
                  type="email"
                  className={styles.input}
                  {...register('contactEmail', { required: true })}
                />
                {errors.contactEmail && (
                  <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
                )}
              </label>

              <label className={styles.field}>
                <span className={styles.label}>{t('portal.accountSettings.contactPhone')}</span>
                <input
                  type="tel"
                  inputMode="tel"
                  className={styles.input}
                  maxLength={20}
                  {...register('contactPhone')}
                />
              </label>

              <label className={`${styles.field} ${styles.fieldFull}`}>
                <span className={styles.label}>{t('portal.accountSettings.address')}</span>
                <input
                  type="text"
                  className={styles.input}
                  maxLength={255}
                  {...register('address')}
                />
              </label>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('portal.accountSettings.notificationsSection')}
            </h2>
            <p className={styles.sectionHint}>{t('portal.accountSettings.notificationsHint')}</p>

            <label className={styles.checkboxField}>
              <input type="checkbox" {...register('notifyEmail')} />
              {t('portal.accountSettings.notifyEmail')}
            </label>
            <label className={styles.checkboxField}>
              <input type="checkbox" {...register('notifyTelegram')} />
              {t('portal.accountSettings.notifyTelegram')}
            </label>
            <label className={styles.checkboxField}>
              <input type="checkbox" {...register('notifyWhatsapp')} />
              {t('portal.accountSettings.notifyWhatsapp')}
            </label>
          </section>

          <button
            type="submit"
            className={styles.saveButton}
            disabled={updateMutation.isPending || !isDirty}
          >
            {updateMutation.isPending && <Spinner />}{' '}
            {updateMutation.isPending
              ? t('portal.accountSettings.saving')
              : t('portal.accountSettings.saveButton')}
          </button>

          <section className={styles.dangerSection}>
            <h2 className={styles.sectionTitle}>{t('portal.accountSettings.dangerSection')}</h2>
            <p className={styles.sectionHint}>{t('portal.accountSettings.dangerIntro')}</p>
            <p className={styles.sectionHint}>{t('portal.accountSettings.dangerKept')}</p>
            <p className={styles.sectionHint}>{t('portal.accountSettings.dangerRemoved')}</p>

            <label className={styles.field}>
              <span className={styles.label}>{t('portal.accountSettings.dangerConfirmLabel')}</span>
              <input
                type="text"
                className={styles.input}
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                autoComplete="off"
              />
            </label>

            <button
              type="button"
              className={styles.dangerButton}
              disabled={
                deleteMutation.isPending || deleteConfirmation !== DELETE_CONFIRMATION_PHRASE
              }
              onClick={handleDelete}
            >
              {deleteMutation.isPending && <Spinner />} {t('portal.accountSettings.dangerButton')}
            </button>
          </section>
        </form>
      )}
    </PortalShell>
  );
}
